// HomePage.js — Dashboard with configurable metric tiles + configurable quick actions.
// Sidebar is provided app-wide by <AppSidebar /> (see App.js), not here.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, Boxes, Car, ClipboardList,
  FileText, Layers, Package, PlusCircle, ScanLine, Settings,
  Truck, Upload, Users, Warehouse, BadgeDollarSign, X,
  CircleCheck, CalendarDays, MapPin, Bell, ScanQrCode, UserPlus2Icon, LucideTractor, Home,
} from 'lucide-react';
import { api } from '../scripts/api';

// ── Metric definitions ─────────────────────────────────────────────────────
// Each metric knows where to get its value from the API response,
// what color to use, and where clicking the tile navigates.

const ALL_METRICS = [
  { id: 'total',         label: 'Total Inventory',    desc: 'All active units',            color: '#3b82f6', to: '/inventory' },
  { id: 'alerts',        label: 'Alerts',             desc: 'Units flagged for attention',  color: '#ef4444', to: '/inventory?color=6' },
  { id: 'in_stock',      label: 'In Stock',           desc: 'Available inventory',          color: '#22c55e', to: '/stock' },
  { id: 'demo',          label: 'Demo Fleet',         desc: 'Active demo units',            color: '#a855f7', to: '/demo' },
  { id: 'rentals',       label: 'Rentals',            desc: 'Rental fleet',                 color: '#f97316', to: '/rentals' },
  { id: 'sold',          label: 'Sold',               desc: 'Closed deals',                 color: '#10b981', to: '/sold' },
  { id: 'on_order',      label: 'On Order',           desc: 'Ordered, not yet received',    color: '#06b6d4', to: '/inventory?color=8' },
  { id: 'to_order',      label: 'To Order',           desc: 'Pending order creation',       color: '#eab308', to: '/inventory?color=7' },
  { id: 'in_transit',    label: 'In Transit',         desc: 'Units currently moving',       color: '#f59e0b', to: '/inventory?color=4,5,13' },
  { id: 'ready_factory', label: 'Ready at Factory',   desc: 'Awaiting pickup',              color: '#64748b', to: '/inventory?color=12' },
  { id: 'pdi_stock',     label: 'PDI / In Stock',     desc: 'Stock & PDI — Calgary',        color: '#0d6efd', to: '/inventory?color=9' },
  { id: 'chassis_alloc', label: 'Chassis Allocated',  desc: 'Allocation slots',             color: '#94a3b8', to: '/inventory?color=11' },
  { id: 'components',    label: 'Components',         desc: 'Total tracked components',     color: '#6366f1', to: '/inventory' },
  { id: 'unassigned',    label: 'Unassigned',         desc: 'No status set',                color: '#9ca3af', to: '/inventory?color=0' },
];

const DEFAULT_METRIC_IDS = [
  'total', 'alerts', 'in_stock', 'demo', 'rentals',
  'sold', 'on_order', 'in_transit', 'ready_factory', 'pdi_stock',
];

const STORAGE_KEY = 'home_metrics_v1';
const MAX_METRICS = 10;

// ── Quick actions: user-configurable list ────────────────────────────────
const ALL_QUICK_ACTIONS = [
  { id: 'add_record',      label: 'Add Record',      to: '/new',                        icon: PlusCircle },
  { id: 'scan_vin',        label: 'Scan VIN',        to: '/vin',                        icon: ScanQrCode },
  { id: 'start_pdi',       label: 'Start PDI',       to: '/pdi',                        icon: ClipboardList },
  { id: 'pdi_sessions',    label: 'PDI Sessions',    to: '/pdi/manage',                 icon: FileText },
  { id: 'pdi_reports',     label: 'PDI Reports',     to: '/pdi/reports',                icon: BarChart3 },
  { id: 'inventory',       label: 'All Inventory',   to: '/inventory',                  icon: Boxes },
  { id: 'stock',           label: 'Stock',           to: '/stock',                      icon: Warehouse },
  { id: 'demo',            label: 'Demo Fleet',      to: '/demo',                       icon: Car },
  { id: 'rentals',         label: 'Rentals',         to: '/rentals',                    icon: BadgeDollarSign },
  { id: 'drive_away',      label: 'Drive-Away',      to: '/admin/moves',                icon: Truck },
  { id: 'drivers_today',   label: 'Drivers Today',   to: '/admin/drivers-today',        icon: CircleCheck },
  { id: 'driver_calendar', label: 'Driver Calendar', to: '/admin/driver-calendar',      icon: CalendarDays },
  { id: 'trip_cost',       label: 'Trip Cost',        to: '/admin/trip-cost',            icon: MapPin },
  { id: 'customers',       label: 'Customers',       to: '/admin/customers/details',    icon: UserPlus2Icon },
  { id: 'vendors',         label: 'Vendors',         to: '/admin/vendors',              icon: LucideTractor },
  { id: 'analytics',       label: 'Analytics',       to: '/reports',                    icon: BarChart3 },
  { id: 'import',          label: 'Import Data',     to: '/admin/import',               icon: Upload },
  { id: 'builder_upload',  label: 'Builder Upload',  to: '/builder-upload',             icon: Package },
  { id: 'notifications',   label: 'Notifications',   to: '/admin/notifications',        icon: Bell },
  { id: 'users',           label: 'Users',           to: '/admin/users',                icon: Users },
];

const DEFAULT_QUICK_ACTION_IDS = ['add_record', 'scan_vin', 'start_pdi', 'drive_away', 'drivers_today', 'analytics'];
const QA_STORAGE_KEY = 'home_quick_actions_v1';
const MAX_QUICK_ACTIONS = 10;

function loadSavedQuickActions() {
  try {
    const raw = localStorage.getItem(QA_STORAGE_KEY);
    if (raw) {
      const ids = JSON.parse(raw);
      if (Array.isArray(ids) && ids.length > 0) return ids.slice(0, MAX_QUICK_ACTIONS);
    }
  } catch (_) {}
  return DEFAULT_QUICK_ACTION_IDS;
}

function loadSavedMetrics() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const ids = JSON.parse(raw);
      if (Array.isArray(ids) && ids.length > 0) return ids.slice(0, MAX_METRICS);
    }
  } catch (_) {}
  return DEFAULT_METRIC_IDS;
}

function computeValue(id, summary, pgStats) {
  const byColor = {};
  (summary?.byColor || []).forEach(({ color_code, n }) => { byColor[String(color_code)] = Number(n); });

  switch (id) {
    case 'total':         return summary?.total ?? null;
    case 'alerts':        return summary?.flags?.alerts ?? byColor['6'] ?? null;
    case 'in_stock':      return summary?.flags?.is_stock ?? null;
    case 'demo':          return summary?.flags?.is_demo ?? byColor['10'] ?? null;
    case 'rentals':       return summary?.flags?.is_rental ?? byColor['2'] ?? null;
    case 'sold':          return byColor['1'] ?? null;
    case 'on_order':      return byColor['8'] ?? null;
    case 'to_order':      return byColor['7'] ?? null;
    case 'in_transit':    return (byColor['4'] ?? 0) + (byColor['5'] ?? 0) + (byColor['13'] ?? 0);
    case 'ready_factory': return byColor['12'] ?? null;
    case 'pdi_stock':     return byColor['9'] ?? null;
    case 'chassis_alloc': return byColor['11'] ?? null;
    case 'components':    return pgStats?.total_components ?? null;
    case 'unassigned':    return byColor['0'] ?? null;
    default:              return null;
  }
}

// ── Metric tile ────────────────────────────────────────────────────────────

function MetricTile({ def, value, loading }) {
  const nav = useNavigate();
  const isNull = value === null || value === undefined;

  return (
    <button
      onClick={() => nav(def.to)}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 14,
        padding: '18px 20px 16px',
        textAlign: 'left',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 120ms, box-shadow 120ms',
        minWidth: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {/* Color accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: def.color, borderRadius: '14px 14px 0 0' }} />

      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.5, marginBottom: 10 }}>
        {def.label}
      </div>

      <div style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, color: def.color, letterSpacing: -1 }}>
        {loading ? <span style={{ fontSize: 22, opacity: 0.4 }}>—</span> : isNull ? <span style={{ fontSize: 20, opacity: 0.3 }}>—</span> : value.toLocaleString()}
      </div>

      <div style={{ fontSize: 11, opacity: 0.45, marginTop: 6 }}>{def.desc}</div>
    </button>
  );
}

// ── Metrics config modal ───────────────────────────────────────────────────

function MetricsModal({ selected, onSave, onClose }) {
  const [draft, setDraft] = useState(selected);

  const toggle = (id) => {
    setDraft((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_METRICS) return prev; // max reached
      return [...prev, id];
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 16, width: 460, maxWidth: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Dashboard Metrics</div>
            <div style={{ fontSize: 12, opacity: 0.55, marginTop: 2 }}>
              {draft.length} / {MAX_METRICS} selected
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={18} /></button>
        </div>

        {/* Metric list */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '10px 20px' }}>
          {ALL_METRICS.map((def) => {
            const checked = draft.includes(def.id);
            const disabled = !checked && draft.length >= MAX_METRICS;
            return (
              <label
                key={def.id}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--card-border)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }}
              >
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(def.id)} style={{ width: 16, height: 16, accentColor: def.color }} />
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: def.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{def.label}</div>
                  <div style={{ fontSize: 11, opacity: 0.55, marginTop: 1 }}>{def.desc}</div>
                </div>
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--card-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => { onSave(draft); onClose(); }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Main dashboard page ────────────────────────────────────────────────────

export default function HomePage() {
  const [selectedIds, setSelectedIds] = useState(loadSavedMetrics);
  const [quickActionIds, setQuickActionIds] = useState(loadSavedQuickActions);
  const [summary, setSummary] = useState(null);
  const [pgStats, setPgStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const [quickConfigOpen, setQuickConfigOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.allSettled([
        api.get('/reports/summary?table=all_inventory'),
        api.get('/pg/stats'),
      ]);
      if (s.status === 'fulfilled') setSummary(s.value.data);
      if (p.status === 'fulfilled') setPgStats(p.value.data);
      setLastRefresh(new Date());
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleSave = (ids) => {
    setSelectedIds(ids);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  };

  const handleSaveQuickActions = (ids) => {
    setQuickActionIds(ids);
    localStorage.setItem(QA_STORAGE_KEY, JSON.stringify(ids));
  };

  const activeDefs = selectedIds
    .map((id) => ALL_METRICS.find((m) => m.id === id))
    .filter(Boolean);

  const activeQuickActions = quickActionIds
    .map((id) => ALL_QUICK_ACTIONS.find((q) => q.id === id))
    .filter(Boolean);

  return (
    <div className="container">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 22, letterSpacing: -0.3 }}>Dashboard</div>
          {lastRefresh && (
            <div style={{ fontSize: 11, opacity: 0.4, marginTop: 2 }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" style={{ fontSize: 12, padding: '6px 12px' }} onClick={fetchMetrics} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            className="btn-outline"
            style={{ padding: '6px 10px' }}
            onClick={() => setConfigOpen(true)}
            title="Configure metrics"
          >
            <Settings size={15} />
          </button>
        </div>
      </div>

      {/* Metric tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: 12, marginBottom: 24 }}>
        {activeDefs.map((def) => (
          <MetricTile
            key={def.id}
            def={def}
            value={computeValue(def.id, summary, pgStats)}
            loading={loading}
          />
        ))}
      </div>

      {/* Quick Actions section */}
      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.4 }}>
            Quick Actions
          </div>
          <button
            className="btn-outline"
            style={{ padding: '4px 10px', fontSize: 11 }}
            onClick={() => setQuickConfigOpen(true)}
            title="Choose which quick actions appear here"
          >
            <Settings size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
            Customize
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {activeQuickActions.map((q) => <QuickActionTile key={q.id} action={q} />)}
          {activeQuickActions.length === 0 && (
            <div className="muted" style={{ fontSize: 13 }}>
              No quick actions selected. Click <strong>Customize</strong> to choose.
            </div>
          )}
        </div>
      </div>

      {configOpen && (
        <MetricsModal
          selected={selectedIds}
          onSave={handleSave}
          onClose={() => setConfigOpen(false)}
        />
      )}

      {quickConfigOpen && (
        <QuickActionsModal
          selected={quickActionIds}
          onSave={handleSaveQuickActions}
          onClose={() => setQuickConfigOpen(false)}
        />
      )}
    </div>
  );
}

function QuickActionTile({ action }) {
  const nav = useNavigate();
  const Icon = action.icon || Home;
  return (
    <button
      onClick={() => nav(action.to)}
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: 10,
        padding: '12px 14px',
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        transition: 'transform 100ms, background 100ms',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgba(127,127,127,0.06)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.background = 'var(--card-bg)'; }}
    >
      <Icon size={18} style={{ opacity: 0.75, flexShrink: 0 }} />
      <span style={{ fontWeight: 700, fontSize: 13 }}>{action.label}</span>
    </button>
  );
}

function QuickActionsModal({ selected, onSave, onClose }) {
  const [draft, setDraft] = useState(selected);
  const toggle = (id) => {
    setDraft((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_QUICK_ACTIONS) return prev;
      return [...prev, id];
    });
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 16, width: 500, maxWidth: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Quick Actions</div>
            <div style={{ fontSize: 12, opacity: 0.55, marginTop: 2 }}>
              {draft.length} / {MAX_QUICK_ACTIONS} selected
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={18} /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '10px 20px' }}>
          {ALL_QUICK_ACTIONS.map((q) => {
            const checked = draft.includes(q.id);
            const disabled = !checked && draft.length >= MAX_QUICK_ACTIONS;
            const Icon = q.icon;
            return (
              <label
                key={q.id}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid var(--card-border)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }}
              >
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggle(q.id)} style={{ width: 16, height: 16 }} />
                <Icon size={16} style={{ opacity: 0.7, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{q.label}</div>
                  <div style={{ fontSize: 11, opacity: 0.55, marginTop: 1 }}>{q.to}</div>
                </div>
              </label>
            );
          })}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--card-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => { onSave(draft); onClose(); }}>Save</button>
        </div>
      </div>
    </div>
  );
}
